import PropTypes from 'prop-types';
import { useState, useCallback, useEffect } from 'react';
import { Autocomplete, Box, Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { useDebounce } from 'use-lodash-debounce';
import { Iconify } from 'src/components';
import { SearchNotFound } from 'src/components/search-not-found';
import { useSelector, dispatch } from 'src/redux/store';
import { searchLead, clearTaskLead, quickCreateLead } from 'src/redux/slices/lead';

const LeadSearchField = ({
  label,
  value,
  onChange,
  onLeadSelect,
  onCreateLead,
  required = false,
  disabled = false,
  placeholder = 'Search for a lead...',
  showCreateButton = true,
}) => {
  const [searchQuery, setSearchQuery] = useState(value || '');
  const [isCreating, setIsCreating] = useState(false);
  const { taskSearchLead, isLoading } = useSelector((state) => state.lead);
  const { practiceId } = useSelector((state) => state.practiceTreatmentList);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Sync searchQuery with value prop when it changes externally
  useEffect(() => {
    if (value && value !== searchQuery) {
      setSearchQuery(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Clear taskSearchLead on unmount
  useEffect(() => () => {
      dispatch(clearTaskLead());
    }, []);

  // Search leads when query changes
  const handleSearch = useCallback((query) => {
    // Ensure query is a string
    const searchString = typeof query === 'string' ? query : '';
    if (searchString && searchString.length >= 2 && practiceId) {
      dispatch(searchLead(searchString, practiceId, 'taskLead'));
    }
  }, [practiceId]);

  // Effect to trigger search on debounced value
  useEffect(() => {
    if (debouncedSearchQuery && typeof debouncedSearchQuery === 'string') {
      handleSearch(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, handleSearch]);

  const handleInputChange = (event, newValue, reason) => {
    if (reason === 'input') {
      // Ensure we're setting a string value
      const stringValue = typeof newValue === 'string' ? newValue : '';
      setSearchQuery(stringValue);
      if (stringValue.length >= 2) {
        handleSearch(stringValue);
      }
    }
  };

  const handleLeadChange = (event, selectedLead) => {
    if (selectedLead && typeof selectedLead === 'object') {
      const fullName = `${selectedLead.first_name || ''} ${selectedLead.last_name || ''}`.trim();
      setSearchQuery(fullName);
      onChange?.(fullName);
      onLeadSelect?.(selectedLead);
    }
  };

  // Parse search query into first and last name
  const parseNameFromQuery = (query) => {
    const trimmed = (query || '').trim();
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' '),
    };
  };

  // Handle create lead button click
  const handleCreateLead = useCallback(() => {
    if (!searchQuery?.trim() || !practiceId || isCreating) return;

    setIsCreating(true);
    const { firstName, lastName } = parseNameFromQuery(searchQuery);

    const leadData = {
      first_name: firstName,
      last_name: lastName,
      lead_contact: { preferred_contact_method: 'PHONE' },
      lead_source: {},
    };

    dispatch(quickCreateLead(
      practiceId,
      leadData,
      (newLead) => {
        setIsCreating(false);
        // Auto-select the newly created lead
        const fullName = `${newLead.first_name || ''} ${newLead.last_name || ''}`.trim();
        setSearchQuery(fullName);
        onChange?.(fullName);
        onLeadSelect?.(newLead);
        onCreateLead?.(newLead);
      },
      () => {
        setIsCreating(false);
      }
    ));
  }, [searchQuery, practiceId, isCreating, onChange, onLeadSelect, onCreateLead]);

  const noOptionsHandler = () => {
    if (searchQuery === '' || (taskSearchLead && taskSearchLead.length === 0)) {
      return <SearchNotFound searchQuery={searchQuery} />;
    }
    return '';
  };

  // Handle create lead button click with proper event handling
  const onCreateLeadClick = useCallback((event) => {
    // Prevent Autocomplete from closing and handling the event
    event.preventDefault();
    event.stopPropagation();
    handleCreateLead();
  }, [handleCreateLead]);

  // Create Lead option component to show at the bottom of results
  const CreateLeadOption = useCallback(() => {
    if (!showCreateButton || !searchQuery?.trim() || searchQuery.trim().length < 2) return null;

    const { firstName, lastName } = parseNameFromQuery(searchQuery);
    const displayName = lastName ? `${firstName} ${lastName}` : firstName;

    return (
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid #E0E0E0',
          bgcolor: '#F9FAFB',
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <Button
          fullWidth
          variant="outlined"
          startIcon={isCreating ? <CircularProgress size={16} /> : <Iconify icon="mdi:plus" />}
          onMouseDown={onCreateLeadClick}
          disabled={isCreating}
          sx={{
            justifyContent: 'flex-start',
            color: '#00A76F',
            borderColor: '#00A76F',
            '&:hover': {
              bgcolor: 'rgba(0,167,111,0.08)',
              borderColor: '#00A76F',
            },
          }}
        >
          {isCreating ? 'Creating...' : `Create lead "${displayName}"`}
        </Button>
      </Box>
    );
  }, [showCreateButton, searchQuery, isCreating, onCreateLeadClick]);

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: '#212B36',
            mb: '8px',
          }}
        >
          {label}
          {required && <span style={{ color: '#FF5630' }}> *</span>}
        </Typography>
      )}
      <Autocomplete
        freeSolo
        disabled={disabled}
        inputValue={searchQuery}
        value={searchQuery}
        clearIcon={false}
        options={taskSearchLead?.length > 0 ? taskSearchLead : []}
        filterOptions={(option) => option}
        loading={isLoading}
        onInputChange={handleInputChange}
        noOptionsText={noOptionsHandler()}
        onChange={handleLeadChange}
        isOptionEqualToValue={(option, val) => option?.first_name === val}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return `${option?.first_name || ''} ${option?.last_name || ''}`.trim();
        }}
        ListboxProps={{
          sx: {
            maxHeight: 300,
            '& .MuiAutocomplete-option:last-child': {
              borderBottom: 'none',
            },
          },
        }}
        ListboxComponent={(listboxProps) => (
          <Box>
            <Box component="ul" {...listboxProps} />
            {CreateLeadOption()}
          </Box>
        )}
        renderOption={(props, option) => {
          const { first_name: firstName, last_name: lastName, lead_contact: leadContact } = option || {};
          const { email, phone } = leadContact || {};
          return (
            <Box
              component="li"
              {...props}
              key={option.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start !important',
                padding: '10px 16px !important',
                borderBottom: '1px solid #F4F6F8',
                '&:hover': { bgcolor: 'rgba(0,167,111,0.08) !important' },
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#212B36' }}>
                {firstName || ''} {lastName || ''}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#637381' }}>
                {email || ''} {phone ? `• ${phone}` : ''}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: '#919EAB', width: 20, height: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                fontSize: 14,
                height: '40px',
                '& fieldset': { borderColor: '#E0E0E0' },
                '&:hover fieldset': { borderColor: '#BDBDBD' },
                '&.Mui-focused fieldset': { borderColor: '#00A76F' },
              },
            }}
          />
        )}
        sx={{ width: '100%' }}
      />
    </Box>
  );
};

LeadSearchField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onLeadSelect: PropTypes.func,
  onCreateLead: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  showCreateButton: PropTypes.bool,
};

export default LeadSearchField;
